import { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { OFFER_CATEGORIES } from '../constants/categories';
import { toSlug } from '../utils/slug';
import { displayLabel } from '../constants/categoryMeta';
import { useOffers } from '../context/OffersContext';
import { useWishlist } from '../utils/wishlist';
import OfferCard from '../components/common/OfferCard';

export default function OffersPage() {
  const navigate = useNavigate();
  const { savedIds, toggleSave } = useWishlist();
  const {
    items, lastEvaluatedKey, hasMore, filter, search, activeSearch,
    loadingOffers, loadingMore, offersError,
    setFilter, setSearch, setActiveSearch,
    fetchOffers,
  } = useOffers();

  const sentinelRef = useRef(null);
  const filterChangeAt = useRef(Date.now());

  useEffect(() => {
    filterChangeAt.current = Date.now();
  }, [filter, activeSearch]);

  useEffect(() => {
    if (!sentinelRef.current) return;

    function maybeLoadMore() {
      if (Date.now() - filterChangeAt.current < 800) return;
      if (!hasMore || loadingMore || loadingOffers) return;
      const el = sentinelRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // Same 600px look-ahead the observer uses, checked directly against the
      // viewport. With only 6 items on the first page the sentinel can already
      // be inside the viewport at mount, and IntersectionObserver only fires on
      // a *change* in intersection — it won't fire again once loading flips
      // from true to false if the element was intersecting the whole time, so
      // the list gets stuck at page one. This runs the same check directly.
      if (rect.top <= window.innerHeight + 600) {
        fetchOffers(filter, activeSearch, lastEvaluatedKey);
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (Date.now() - filterChangeAt.current < 800) return;
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loadingOffers) {
          fetchOffers(filter, activeSearch, lastEvaluatedKey);
        }
      },
      { threshold: 0, rootMargin: '600px 0px' }
    );
    observer.observe(sentinelRef.current);

    let ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        maybeLoadMore();
        ticking = false;
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    // Run once immediately in case the sentinel is already in view.
    maybeLoadMore();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [hasMore, loadingMore, loadingOffers, lastEvaluatedKey, filter, activeSearch, fetchOffers]);

  const types = ['All', ...OFFER_CATEGORIES];

  return (
    <div className="App">
      <Helmet>
        <title>Flipkart, Amazon, Myntra &amp; Ajio Offers Today | All Deals — OfferLelo</title>
        <meta name="description" content="Browse today's best Flipkart offers, Amazon deals, Myntra coupons &amp; Ajio discounts in India. Verified offers on Electronics, Fashion, Mobiles, Food, Travel &amp; Credit Cards — updated daily at OfferLelo." />
        <meta name="keywords" content="flipkart offer, amazon offer, myntra offer, ajio offer, flipkart deals today, amazon deals today, myntra coupons, ajio discount, online shopping offers India, electronics offers, fashion deals, mobile offers, credit card offers, cashback offers" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />
        <meta name="geo.region" content="IN" />
        <meta property="og:title" content="Flipkart, Amazon, Myntra & Ajio Offers Today | All Deals — OfferLelo" />
        <meta property="og:description" content="Browse today's best Flipkart, Amazon, Myntra & Ajio offers & deals in India — verified daily at OfferLelo." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.offerlelo.com/offers" />
        <meta property="og:image" content="https://www.offerlelo.com/logo192.png" />
        <meta property="og:site_name" content="OfferLelo" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Flipkart, Amazon, Myntra & Ajio Offers Today | OfferLelo" />
        <meta name="twitter:description" content="Today's best Flipkart, Amazon, Myntra & Ajio offers & deals in India — verified daily." />
        <meta name="twitter:image" content="https://www.offerlelo.com/logo192.png" />
        <link rel="canonical" href="https://www.offerlelo.com/offers" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "All Offers & Deals — Flipkart, Amazon, Myntra, Ajio",
          "url": "https://www.offerlelo.com/offers",
          "description": "Today's best Flipkart, Amazon, Myntra and Ajio offers & deals in India — verified daily at OfferLelo.",
          "inLanguage": "en-IN",
          "isPartOf": { "@type": "WebSite", "url": "https://www.offerlelo.com", "name": "OfferLelo" },
          "about": [
            { "@type": "Thing", "name": "Flipkart Offers" },
            { "@type": "Thing", "name": "Amazon Offers" },
            { "@type": "Thing", "name": "Myntra Offers" },
            { "@type": "Thing", "name": "Ajio Offers" }
          ]
        })}</script>
      </Helmet>

      <header className="modern-header">
        <div className="header-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <img src="/logo.svg" alt="OfferLelo Logo" />
          <span className="header-logo-text">OfferLelo</span>
        </div>
        <div className="header-actions">
          <button className="admin-btn" onClick={() => navigate('/blogs')}>📝 Blogs</button>
        </div>
      </header>

      <form
        onSubmit={e => { e.preventDefault(); setActiveSearch(search.trim()); }}
        style={{ maxWidth: 440, margin: '18px auto', display: 'flex', gap: 8, alignItems: 'center', padding: '0 12px' }}
      >
        <div style={{ flex: 1, position: 'relative' }}>
          <input
            type="text"
            placeholder="Search offers..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', padding: '10px 36px 10px 12px', borderRadius: 8, border: '1.5px solid #a5b4fc', fontSize: '1rem', boxSizing: 'border-box', outline: 'none' }}
          />
          {search && (
            <button type="button" onClick={() => { setSearch(''); setActiveSearch(''); }}
              style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '1.1rem', lineHeight: 1 }}>
              ✕
            </button>
          )}
        </div>
        <button type="submit" style={{ padding: '10px 18px', borderRadius: 8, background: '#6366f1', color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
          Search
        </button>
      </form>

      {activeSearch && (
        <div style={{ textAlign: 'center', color: '#6366f1', marginBottom: 10, fontSize: '0.95rem' }}>
          Showing results for <strong>"{activeSearch}"</strong>
          <button onClick={() => { setSearch(''); setActiveSearch(''); }} style={{ marginLeft: 10, background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline' }}>
            Clear
          </button>
        </div>
      )}

      <div className="filter-bar-wrap">
        <div className="filter-bar">
          {types.map(type => (
            <button key={type} className={filter === type ? 'filter-btn active' : 'filter-btn'} onClick={() => setFilter(type)}>
              {type}
            </button>
          ))}
        </div>
      </div>

      {/* The filter bar above is <button>s that mutate React state, so a
          crawler cannot follow it anywhere. These are real <a> links to the
          category hub pages (/offers/electronics, ...) and are the only crawl
          path from this listing into a category. Keep both: the buttons filter
          in place for users, the links exist for discovery. */}
      <nav
        aria-label="Offer categories"
        style={{ maxWidth: 1400, margin: '10px auto 0', padding: '0 20px', display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}
      >
        {OFFER_CATEGORIES.map(c => (
          <a
            key={c}
            href={`/offers/${toSlug(c)}`}
            style={{ fontSize: '0.86rem', color: '#6366f1', textDecoration: 'none', border: '1px solid #c7d2fe', borderRadius: 999, padding: '6px 14px', background: '#fff' }}
          >
            {displayLabel(c)} Offers
          </a>
        ))}
      </nav>

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '0 12px 60px' }}>
        {loadingOffers && (
          <div className="grid-container">
            {Array.from({ length: 8 }).map((_, i) => (
              <div className="skeleton-card" key={i}>
                <div className="skeleton skeleton-card-img" />
                <div className="skeleton-card-body">
                  <div className="skeleton skeleton-card-line wide" />
                  <div className="skeleton skeleton-card-line mid" />
                  <div className="skeleton skeleton-card-line short" />
                </div>
              </div>
            ))}
          </div>
        )}
        {offersError && <div style={{ color: 'red', textAlign: 'center', padding: 12 }}>Error: {offersError}</div>}
        {!loadingOffers && !offersError && (
          <>
            <div className="grid-container">
              {items.length === 0 ? (
                <div className="no-items">No offers found.</div>
              ) : (
                items.map(item => (
                  <OfferCard
                    key={item.id}
                    item={item}
                    savedIds={savedIds}
                    onToggleSave={toggleSave}
                  />
                ))
              )}
            </div>
            <div ref={sentinelRef} style={{ height: 32 }} />
            {loadingMore && (
              <div style={{ textAlign: 'center', padding: '12px 0', color: '#6366f1', fontWeight: 600, fontSize: '0.95rem' }}>
                Loading...
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
